import React, { createContext, useContext, useState, useCallback } from 'react';

const SubscriptionConfigContext = createContext();

export const useSubscriptionConfig = () => {
  const context = useContext(SubscriptionConfigContext);
  if (!context) {
    throw new Error('useSubscriptionConfig must be used within SubscriptionConfigProvider');
  }
  return context;
};

export const SubscriptionConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    globalDiscount: false,
    extraDiscountPercentage: 0,
    discountScope: 'all', // 'all', 'selected', 'specific'
    selectedPackages: [],
    
    coolingOffPeriod: {
      enabled: true,
      days: 14,
      refundPercentage: 100
    },
    
    renewal: {
      autoRenew: true,
      gracePeriodDays: 7,
      reminderDaysBefore: 3
    },
    
    cancellation: {
      allowed: false,
      allowedWithinDays: 30,
      refundPercentage: 0,
      adminApprovalRequired: false
    },
    
    billing: {
      proRataRefund: false,
      billingCycles: 0, // 0 = indefinite
      trialPeriodDays: 0
    },
    
    countrySpecificRules: {},
    durationSpecificRules: {}
  });

  const [loading, setLoading] = useState(false);
  const [countryRules, setCountryRules] = useState({});
  const [durationRules, setDurationRules] = useState({});
  const [hasLoadedRules, setHasLoadedRules] = useState(false);

  const fetchCountryRules = useCallback(async (countryId) => {
    // API call to fetch country rules
    // This would be implemented later
    return { defaultCoolingOffPeriod: 14 };
  }, []);

  const fetchDurationRules = useCallback(async (durationId) => {
    // API call to fetch duration rules
    // This would be implemented later
    return { defaultRenewalSettings: { gracePeriodDays: 7, reminderDaysBefore: 3 } };
  }, []);

  const updateConfig = useCallback((key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const updateNestedConfig = useCallback((path, value) => {
    setConfig(prev => {
      const keys = path.split('.');
      const newConfig = { ...prev };
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  }, []);

  const loadRules = useCallback(async (countryId, durationId) => {
    if (!countryId || !durationId) return;
    if (hasLoadedRules && countryId === config.countrySpecificRules?.countryId && 
        durationId === config.durationSpecificRules?.durationId) {
      return; // Already loaded
    }

    setLoading(true);
    try {
      // In production, these would be API calls
      const countryData = await fetchCountryRules(countryId);
      const durationData = await fetchDurationRules(durationId);
      
      setCountryRules(countryData);
      setDurationRules(durationData);
      
      // Apply default rules from country/duration
      if (countryData.defaultCoolingOffPeriod) {
        updateNestedConfig('coolingOffPeriod.days', countryData.defaultCoolingOffPeriod);
      }
      
      if (durationData.defaultRenewalSettings) {
        Object.entries(durationData.defaultRenewalSettings).forEach(([key, value]) => {
          updateNestedConfig(`renewal.${key}`, value);
        });
      }

      setHasLoadedRules(true);
      updateConfig('countrySpecificRules', { ...countryData, countryId });
      updateConfig('durationSpecificRules', { ...durationData, durationId });
    } catch (error) {
      console.error('Error loading rules:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchCountryRules, fetchDurationRules, updateNestedConfig, updateConfig, hasLoadedRules, config.countrySpecificRules?.countryId, config.durationSpecificRules?.durationId]);

  const resetConfig = useCallback(() => {
    setConfig({
      globalDiscount: false,
      extraDiscountPercentage: 0,
      discountScope: 'all',
      selectedPackages: [],
      coolingOffPeriod: {
        enabled: true,
        days: 14,
        refundPercentage: 100
      },
      renewal: {
        autoRenew: true,
        gracePeriodDays: 7,
        reminderDaysBefore: 3
      },
      cancellation: {
        allowed: false,
        allowedWithinDays: 30,
        refundPercentage: 0,
        adminApprovalRequired: false
      },
      billing: {
        proRataRefund: false,
        billingCycles: 0,
        trialPeriodDays: 0
      },
      countrySpecificRules: {},
      durationSpecificRules: {}
    });
    setHasLoadedRules(false);
  }, []);

  const value = {
    config,
    loading,
    countryRules,
    durationRules,
    updateConfig,
    updateNestedConfig,
    loadRules,
    resetConfig,
    hasLoadedRules
  };

  return (
    <SubscriptionConfigContext.Provider value={value}>
      {children}
    </SubscriptionConfigContext.Provider>
  );
};
